'use client';

import { useState } from 'react';

interface PrivacyPolicyClientProps {
  initialTab: string;
}

export function PrivacyPolicyClient({ initialTab }: PrivacyPolicyClientProps) {
  const [activeTab, setActiveTab] = useState<'hotel' | 'theatre'>(
    initialTab === 'theatre' ? 'theatre' : 'hotel'
  );

  const handleTabChange = (newTab: 'hotel' | 'theatre') => {
    setActiveTab(newTab);
    const url = new URL(window.location.href);
    url.searchParams.set('tab', newTab);
    window.history.pushState({}, '', url.pathname + url.search);
  };

  return (
    <main className="mb-5 pb-5">
      <style>{`
        .tabs2 {
          display: flex;
          gap: 15px;
          margin-bottom: 30px;
        }
        .tab2 {
          padding: 12px 20px;
          border-radius: 30px;
          border: none;
          cursor: pointer;
          background: #6b6b6b;
          color: #fff;
          font-weight: 500;
          transition: 0.3s;
          position: relative;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .tab2 img {
          width: 24px;
          height: auto;
        }
        .tab2.active {
          background: #4b0d5c;
        }
        .tab2.active::after {
          content: "";
          position: absolute;
          bottom: -10px;
          left: 50%;
          transform: translateX(-50%);
          border-width: 10px 10px 0;
          border-style: solid;
          border-color: #4b0d5c transparent transparent transparent;
        }
        .tab-content {
          display: none;
        }
        .tab-content.active {
          display: block;
        }
        .tab-content h2 {
          margin-bottom: 10px;
        }
      `}</style>

      {/* Breadcrumb Section */}
      <section className="container-fluid bg-purple-light hidden sm:block" style={{ backgroundColor: '#dc9cf7' }}>
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <a href="/" link-type="url">
                      Home
                    </a>
                  </li>
                  <li aria-current="page" className="breadcrumb-item active">
                    Privacy Policy
                  </li>
                </ol>
              </nav>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="pt-4">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="tabs-container">
                {/* Tabs */}
                <div className="tabs2">
                  <div
                    onClick={() => handleTabChange('hotel')}
                    className={`tab2 ${activeTab === 'hotel' ? 'active' : ''}`}
                    data-tab="hotel"
                  >
                    <img src="https://test-bookitplease.optimiser.site/image/hotel-p-policy.png" alt="Eat Sleep Enjoy" /> Hotels
                  </div>
                  <div
                    onClick={() => handleTabChange('theatre')}
                    className={`tab2 ${activeTab === 'theatre' ? 'active' : ''}`}
                    data-tab="theatre"
                  >
                    <img src="https://test-bookitplease.optimiser.site/image/theatre-p-policy.png" alt="Eat Sleep Enjoy" /> Theatre
                  </div>
                </div>

                {/* Content */}
                <div role="tabpanel" id="hotel" className={`tab-content ${activeTab === 'hotel' ? 'active' : ''}`}>
                  <div>
                    <h2>Hotels Privacy Policy</h2>
                    <p>Last updated July 24, 2025</p>

                    <p className="pt-2">
                      This Privacy Notice for GHS Global Hospitality Ltd t/a Book It Please (doing business
                      as Book It Please) (&apos;we&apos;, &apos;us&apos;, or &apos;our&apos;), describes how
                      and why we might access, collect, store, use, and/or share (&apos;process&apos;) your
                      personal information when you use our services (&apos;Services&apos;), including when you:
                    </p>
                    <ul>
                      <li>
                        Visit our website at{' '}
                        <a href="https://www.bookitplease.com/" link-type="url">
                          http://www.bookitplease.com
                        </a>{' '}
                        or any website of ours that links to this Privacy Notice
                      </li>
                      <li>Engage with us in other related ways, including any sales, marketing, or events</li>
                    </ul>
                    <p>
                      <b>Questions or concerns?</b> Reading this Privacy Notice will help you
                      understand your privacy rights and choices. We are responsible for making decisions
                      about how your personal information is processed. If you do not agree with our policies
                      and practices, please do not use our Services. If you still have any questions or concerns,
                      please contact us at{' '}
                      <a href="mailto:reservations@bookitplease.com" link-type="email">
                        reservations@bookitplease.com
                      </a>
                    </p>

                    <h5 className="pt-3">SUMMARY OF KEY POINTS</h5>
                    <p className="fontweight600">
                      <i>
                        This summary provides key points from our Privacy Notice, but you can find out more
                        details about any of these topics by clicking the link following each key point or by
                        using our table of contents below to find the section you are looking for.
                      </i>
                    </p>
                    <p>
                      <b>What personal information do we process?</b> When you visit, use, or navigate
                      our Services, we may process personal information depending on how you interact with us
                      and the Services, the choices you make, and the products and features you use. Learn more
                      about <a href="#informationcollect">What Information We Collect?</a>
                    </p>
                    <p>
                      <b>Do we process any sensitive personal information?</b> Some of the information
                      may be considered &apos;special&apos; or &apos;sensitive&apos; in certain jurisdictions, for example
                      your racial or ethnic origins, sexual orientation, and religious beliefs. We do not
                      process sensitive personal information.
                    </p>
                    <p>
                      <b>Do we collect any information from third parties?</b> We do not collect any
                      information from third parties.
                    </p>
                    <p>
                      <b>How do we process your information?</b> We process your information to
                      provide, improve, and administer our Services, communicate with you, for security and
                      fraud prevention, and to comply with law. We may also process your information for other
                      purposes with your consent. We process your information only when we have a valid legal
                      reason to do so.
                    </p>
                    <p>
                      <b>In what situations and with which parties do we share personal information?</b>{' '}
                      We may share information in specific situations and with specific third parties.
                    </p>
                    <p>
                      <b>How do we keep your information safe?</b> We have adequate organisational and
                      technical processes and procedures in place to protect your personal information. However,
                      no electronic transmission over the internet or information storage technology can be
                      guaranteed to be 100% secure, so we cannot promise or guarantee that hackers,
                      cybercriminals, or other unauthorised third parties will not be able to defeat our
                      security and improperly collect, access, steal, or modify your information.
                    </p>
                    <p>
                      <b>What are your rights?</b> Depending on where you are located geographically, the
                      applicable privacy law may mean you have certain rights regarding your personal information.
                    </p>
                    <p>
                      <b>How do you exercise your rights?</b> The easiest way to exercise your rights is
                      by submitting a{' '}
                      <a href="https://docs.google.com/document/d/1hQkBpjoiIjMwewNx6NVtFI2Xl2X3NzcDmXZXaz_UV04/edit?usp=sharing" link-type="url">
                        data subject access request
                      </a>
                      , or by contacting us. We will consider and act upon any request in accordance with
                      applicable data protection laws.
                    </p>

                    <h5 className="pt-3">TABLE OF CONTENTS</h5>
                    <ol>
                      <li>
                        <a href="#informationcollect" link-type="url">
                          WHAT INFORMATION DO WE COLLECT?
                        </a>
                      </li>
                      <li>
                        <a href="#informationprocess" link-type="url">
                          HOW DO WE PROCESS YOUR INFORMATION?
                        </a>
                      </li>
                      <li>
                        <a href="#legalbases" link-type="url">
                          WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR PERSONAL INFORMATION?
                        </a>
                      </li>
                      <li>
                        <a href="#personalinformation" link-type="url">
                          WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
                        </a>
                      </li>
                      <li>
                        <a href="#trackingtech" link-type="url">
                          DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
                        </a>
                      </li>
                      <li>
                        <a href="#sociallogin" link-type="url">
                          HOW DO WE HANDLE YOUR SOCIAL LOGINS?
                        </a>
                      </li>
                      <li>
                        <a href="#information" link-type="url">
                          HOW LONG DO WE KEEP YOUR INFORMATION?
                        </a>
                      </li>
                      <li>
                        <a href="#informationsafe" link-type="url">
                          HOW DO WE KEEP YOUR INFORMATION SAFE?
                        </a>
                      </li>
                      <li>
                        <a href="#informationminors" link-type="url">
                          DO WE COLLECT INFORMATION FROM MINORS?
                        </a>
                      </li>
                      <li>
                        <a href="#privacyrights" link-type="url">
                          WHAT ARE YOUR PRIVACY RIGHTS?
                        </a>
                      </li>
                      <li>
                        <a href="#controls" link-type="url">
                          CONTROLS FOR DO-NOT-TRACK FEATURES
                        </a>
                      </li>
                      <li>
                        <a href="#usresidents" link-type="url">
                          DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
                        </a>
                      </li>
                      <li>
                        <a href="#specificprivacy" link-type="url">
                          DO OTHER REGIONS HAVE SPECIFIC PRIVACY RIGHTS?
                        </a>
                      </li>
                      <li>
                        <a href="#updatenotice" link-type="url">
                          DO WE MAKE UPDATES TO THIS NOTICE?
                        </a>
                      </li>
                      <li>
                        <a href="#contactnotice" link-type="url">
                          HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
                        </a>
                      </li>
                      <li>
                        <a href="#data" link-type="url">
                          HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
                        </a>
                      </li>
                    </ol>

                    <h4 id="informationcollect" className="pt-3">
                      1. WHAT INFORMATION DO WE COLLECT?
                    </h4>
                    <p>
                      <b>Personal information you disclose to us</b>
                    </p>
                    <p>
                      <i>
                        <b>In Short:</b> We collect personal information that you provide to us.
                      </i>
                    </p>
                    <p>
                      <i>
                        We collect personal information that you voluntarily provide to us when you
                        register on the Services, express an interest in obtaining information about
                        us or our products and Services, when you participate in activities on the
                        Services, or otherwise when you contact us.
                      </i>
                    </p>
                    <p>
                      <b>Personal Information Provided by You.</b> The personal information that we collect
                      depends on the context of your interactions with us and the Services, the choices you make,
                      and the products and features you use. The personal information we collect may include the
                      following:
                    </p>
                    <ul>
                      <li>names</li>
                      <li>phone numbers</li>
                      <li>email addresses</li>
                      <li>mailing addresses</li>
                      <li>usernames</li>
                      <li>passwords</li>
                      <li>contact preferences</li>
                      <li>contact or authentication data</li>
                      <li>billing addresses</li>
                      <li>debit/credit card numbers</li>
                    </ul>
                    <h5>Sensitive Information. We do not process sensitive information.</h5>
                    <p>
                      <b>Payment Data.</b> We may collect data necessary to process your payment if you choose
                      to make purchases, such as your payment instrument number, and the security code associated
                      with your payment instrument. All payment data is handled and stored by Adyen. You may find
                      their privacy notice link here:{' '}
                      <a href="https://www.adyen.com/policies-and-disclaimer/responsible-disclosure" link-type="url">
                        https://www.adyen.com/policies-and-disclaimer/responsible-disclosure.
                      </a>
                    </p>
                    <p>
                      <b>Social Media Login Data.</b> We may provide you with the option to register with us
                      using your existing social media account details, like your Facebook, X, or other social
                      media account. If you choose to register in this way, we will collect certain profile
                      information about you from the social media provider, as described in the section called{' '}
                      <a href="#sociallogin">&apos;HOW DO WE HANDLE YOUR SOCIAL LOGINS?&apos;</a> below.
                    </p>
                    <p>
                      All personal information that you provide to us must be true, complete, and accurate, and
                      you must notify us of any changes to such personal information.
                    </p>
                    <h5>Information automatically collected</h5>
                    <p>
                      <i>
                        <b>In Short:</b> Some information — such as your Internet Protocol (IP) address
                        and/or browser and device characteristics — is collected automatically when you visit
                        our Services.
                      </i>
                    </p>
                    <p>
                      We automatically collect certain information when you visit, use, or navigate the Services.
                      This information does not reveal your specific identity (like your name or contact
                      information) but may include device and usage information, such as your IP address,
                      browser and device characteristics, operating system, language preferences, referring URLs,
                      device name, country, location, information about how and when you use our Services, and
                      other technical information. This information is primarily needed to maintain the security
                      and operation of our Services, and for our internal analytics and reporting purposes.
                    </p>
                    <p>
                      Like many businesses, we also collect information through cookies and similar technologies.
                      The information we collect includes:
                    </p>
                    <ul>
                      <li>
                        Log and Usage Data: Log and usage data is service-related, diagnostic, usage, and
                        performance information our servers automatically collect when you access or use our
                        Services and which we record in log files. Depending on how you interact with us, this
                        log data may include your IP address, device information, browser type, and settings and
                        information about your activity in the Services (such as the date/time stamps associated
                        with your usage, pages and files viewed, searches, and other actions you take such as which
                        features you use), device event information (such as system activity, error reports
                        (sometimes called &apos;crash dumps&apos;), and hardware settings).
                      </li>
                      <li>
                        Device Data: We collect device data such as information about your computer, phone,
                        tablet, or other device you use to access the Services. Depending on the device used,
                        this device data may include information such as your IP address (or proxy server),
                        device and application identification numbers, location, browser type, hardware model,
                        Internet service provider and/or mobile carrier, operating system, and system
                        configuration information.
                      </li>
                      <li>
                        Location Data: We collect location data such as information about your device&apos;s
                        location, which can be either precise or imprecise. How much information we collect
                        depends on the type and settings of the device you use to access the Services. For
                        example, we may use GPS and other technologies to collect geolocation data that tells us
                        your current location (based on your IP address). You can opt out of allowing us to
                        collect this information either by refusing access to the information or by disabling
                        your Location setting on your device. However, if you choose to opt out, you may not be
                        able to use certain aspects of the Services.
                      </li>
                    </ul>
                    <h5>Google API</h5>
                    <p>
                      Our use of information received from{' '}
                      <a href="https://developers.google.com/terms/api-services-user-data-policy" link-type="url">
                        Google APIs will adhere to Google API Services User Data Policy
                      </a>
                      , including the{' '}
                      <a href="https://developers.google.com/terms/api-services-user-data-policy#limited-use" link-type="url">
                        Limited Use requirements
                      </a>
                      .
                    </p>

                    <h4 id="informationprocess" className="pt-3">
                      2. HOW DO WE PROCESS YOUR INFORMATION?
                    </h4>
                    <p>
                      <i>
                        <b>In Short:</b> We process your information to provide, improve, and administer our
                        Services, communicate with you, for security and fraud prevention, and to comply with law.
                        We process the personal information for the following purposes listed below. We may also
                        process your information for other purposes only with your prior explicit consent.
                      </i>
                    </p>
                    <p>
                      <b>
                        We process your personal information for a variety of reasons, depending on how you
                        interact with our Services, including:
                      </b>
                    </p>
                    <ul>
                      <li>
                        <b>To facilitate account creation and authentication and otherwise manage user accounts.</b>{' '}
                        We may process your information so you can create and log in to your account, as well as
                        keep your account in working order.
                      </li>
                      <li>
                        <b>To deliver and facilitate delivery of services to the user.</b> We may process your
                        information to provide you with the requested service.
                      </li>
                      <li>
                        <b>To respond to user inquiries/offer support to users.</b> We may process your
                        information to respond to your inquiries and solve any potential issues you might have
                        with the requested service.
                      </li>
                      <li>
                        <b>To send administrative information to you.</b> We may process your information to
                        send you details about our products and services, changes to our terms and policies, and
                        other similar information.
                      </li>
                      <li>
                        <b>To fulfil and manage your orders.</b> We may process your information to fulfil and
                        manage your orders, payments, returns, and exchanges made through the Services.
                      </li>
                      <li>
                        <b>To request feedback.</b> We may process your information when necessary to request
                        feedback and to contact you about your use of our Services.
                      </li>
                      <li>
                        <b>To send you marketing and promotional communications.</b> We may process the personal
                        information you send to us for our marketing purposes, if this is in accordance with your
                        marketing preferences. You can opt out of our marketing emails at any time. For more
                        information, see <a href="#privacyrights">&apos;WHAT ARE YOUR PRIVACY RIGHTS?&apos;</a>{' '}
                        below.
                      </li>
                      <li>
                        <b>To deliver targeted advertising to you.</b> We may process your information to
                        develop and display personalised content and advertising tailored to your interests,
                        location, and more.
                      </li>
                      <li>
                        <b>To determine the effectiveness of our marketing and promotional campaigns.</b> We may
                        process your information to better understand how to provide marketing and promotional
                        campaigns that are most relevant to you.
                      </li>
                      <li>
                        <b>To save or protect an individual&apos;s vital interest.</b> We may process your
                        information when necessary to save or protect an individual&rsquo;s vital interest, such
                        as to prevent harm.
                      </li>
                    </ul>

                    <h4 id="legalbases" className="pt-3">
                      3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR INFORMATION?
                    </h4>
                    <p>
                      <i>
                        <b>In Short:</b> We only process your personal information when we believe it is
                        necessary and we have a valid legal reason (i.e. legal basis) to do so under applicable
                        law, like with your consent, to comply with laws, to provide you with services to enter into
                        or fulfil our contractual obligations, to protect your rights, or to fulfil our
                        legitimate business interests.
                      </i>
                    </p>
                    <h6>If you are located in the EU or UK, this section applies to you.</h6>
                    <p>
                      The General Data Protection Regulation (GDPR) and UK GDPR require us to explain the valid
                      legal bases we rely on in order to process your personal information. As such, we may rely on
                      the following legal bases to process your personal information:
                    </p>
                    <ul>
                      <li>
                        <b>Consent:</b> We may process your information if you have given us permission (i.e.
                        consent) to use your personal information for a specific purpose. You can withdraw your
                        consent at any time. Learn more about{' '}
                        <a href="#informationminors">withdrawing your consent</a>.
                      </li>
                      <li>
                        <b>Performance of a Contract:</b> We may process your personal information when we
                        believe it is necessary to fulfil our contractual obligations to you, including providing
                        our Services or at your request prior to entering into a contract with you.
                      </li>
                      <li>
                        <b>Legitimate Interests:</b> We may process your information when we believe it is
                        reasonably necessary to achieve our legitimate business interests and those interests do
                        not outweigh your interests and fundamental rights and freedoms. For example, we may
                        process your personal information for some of the purposes described in order to:
                        <ul>
                          <li>Send users information about special offers and discounts on our products and services</li>
                          <li>Develop and display personalised and relevant advertising content for our users</li>
                          <li>Support our marketing activities</li>
                          <li>Understand how our users use our products and services so we can improve user experience</li>
                        </ul>
                      </li>
                      <li>
                        <b>Legal Obligations:</b> We may process your information where we believe it is
                        necessary for compliance with our legal obligations, such as to cooperate with a law
                        enforcement body or regulatory agency, exercise or defend our legal rights, or disclose your
                        information as evidence in litigation in which we are involved.
                      </li>
                      <li>
                        <b>Vital Interests:</b> We may process your information where we believe it is
                        necessary to protect your vital interests or the vital interests of a third party, such
                        as situations involving potential threats to the safety of any person.
                      </li>
                    </ul>
                    <p>
                      <u>If you are located in Canada, this section applies to you.</u>
                    </p>
                    <p>
                      We may process your information if you have given us specific permission (i.e. express
                      consent) to use your personal information for a specific purpose, or in situations where
                      your permission can be inferred (i.e. implied consent). You can{' '}
                      <a href="#informationminors">withdraw your consent</a> at any time.
                    </p>
                    <p>
                      In some exceptional cases, we may be legally permitted under applicable law to process your
                      information without your consent, including, for example:
                    </p>
                    <ul>
                      <li>If collection is clearly in the interests of an individual and consent cannot be obtained in a timely way</li>
                      <li>For investigations and fraud detection and prevention</li>
                      <li>For business transactions provided certain conditions are met</li>
                      <li>If it is contained in a witness statement and the collection is necessary to assess, process, or settle an insurance claim</li>
                      <li>For identifying injured, ill, or deceased persons and communicating with next of kin</li>
                      <li>If we have reasonable grounds to believe an individual has been, is, or may be victim of financial abuse</li>
                      <li>If it is reasonable to expect collection and use with consent would compromise the availability or the accuracy of the information and the collection is reasonable for purposes related to investigating a breach of an agreement or a contravention of the laws of Canada or a province</li>
                      <li>If disclosure is required to comply with a subpoena, warrant, court order, or rules of the court relating to the production of records</li>
                      <li>If it was produced by an individual in the course of their employment, business, or profession and the collection is consistent with the purposes for which the information was produced</li>
                      <li>If the collection is solely for journalistic, artistic, or literary purposes</li>
                      <li>If the information is publicly available and is specified by the regulations</li>
                      <li>We may disclose de-identified information for approved research or statistics projects, subject to ethics oversight and confidentiality commitments</li>
                    </ul>

                    <h4 id="personalinformation" className="pt-3">
                      4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
                    </h4>
                    <p>
                      <i>
                        <b>In Short:</b> We may share information in specific situations described in this
                        section and/or with the following third parties.
                      </i>
                    </p>
                    <p>We may need to share your personal information in the following situations:</p>
                    <ul>
                      <li>
                        <b>Business Transfers:</b> We may share or transfer your information in connection
                        with, or during negotiations of, any merger, sale of company assets, financing, or
                        acquisition of all or a portion of our business to another company.
                      </li>
                      <li>
                        <b>When we use Google Maps Platform APIs:</b> We may share your information with
                        certain Google Maps Platform APIs (e.g. Google Maps API, Places API). Google Maps uses GPS,
                        Wi-Fi, and cell towers to estimate your location. GPS is accurate to about 20 meters, while
                        Wi-Fi and cell towers help improve accuracy when GPS signals are weak, like indoors. This
                        data helps Google Maps provide directions, but it is not always perfectly precise. We
                        obtain and store on your device (&apos;cache&apos;) your location. You may revoke your
                        consent anytime by contacting us at the contact details provided at the end of this
                        document.
                      </li>
                      <li>
                        <b>Affiliates:</b> We may share your information with our affiliates, in which case we
                        will require those affiliates to honour this Privacy Notice. Affiliates include our parent
                        company and any subsidiaries, joint venture partners, or other companies that we control or
                        that are under common control with us.
                      </li>
                      <li>
                        <b>Business Partners:</b> We may share your information with our business partners to
                        offer you certain products, services, or promotions.
                      </li>
                    </ul>

                    <h4 id="trackingtech" className="pt-3">
                      5. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
                    </h4>
                    <p>
                      <i>
                        <b>In Short:</b> We may use cookies and other tracking technologies to collect and
                        store your information.
                      </i>
                    </p>
                    <p>
                      We may use cookies and similar tracking technologies (like web beacons and pixels) to gather
                      information when you interact with our Services. Some online tracking technologies help us
                      maintain the security of our Services and your account, prevent crashes, fix bugs, save your
                      preferences, and assist with basic site functions.
                    </p>
                    <p>
                      We also permit third parties and service providers to use online tracking technologies on
                      our Services for analytics and advertising, including to help manage and display
                      advertisements, to tailor advertisements to your interests, or to send abandoned shopping
                      cart reminders (depending on your communication preferences). The third parties and service
                      providers use their technology to provide advertising about products and services tailored to
                      your interests which may appear either on our Services or on other websites.
                    </p>
                    <p>
                      To the extent these online tracking technologies are deemed to be a
                      &apos;sale&apos;/&apos;sharing&apos; (which includes targeted advertising, as defined under the
                      applicable laws) under applicable US state laws, you can opt out of these online tracking
                      technologies by submitting a request as described below under section{' '}
                      <a href="#privacyrights">&apos;DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?&apos;</a>
                    </p>
                    <p>
                      Specific information about how we use such technologies and how you can refuse certain
                      cookies is set out in our Cookie Notice.
                    </p>
                    <h5>Google Analytics</h5>
                    <p>
                      We may share your information with Google Analytics to track and analyse the use of the
                      Services. The Google Analytics Advertising Features that we may use include: Remarketing with
                      Google Analytics, Google Display Network Impressions Reporting and Google Analytics Demographics
                      and Interests Reporting. To opt out of being tracked by Google Analytics across the Services,
                      visit <a href="https://tools.google.com/dlpage/gaoptout" link-type="url">https://tools.google.com/dlpage/gaoptout</a>. You can opt out
                      of Google Analytics Advertising Features through <a href="https://myadcenter.google.com/home?sasb=true&amp;ref=ad-settings" link-type="url">Ads Settings</a>{' '}
                      and Ad Settings for mobile apps. Other opt out means include{' '}
                      <a href="https://optout.networkadvertising.org/?c=1" link-type="url">
                        http://optout.networkadvertising.org/
                      </a>{' '}
                      and{' '}
                      <a href="https://thenai.org//opt-out/mobile-opt-out/" link-type="url">
                        http://www.networkadvertising.org/mobile-choice
                      </a>
                      . For more information on the privacy practices of Google, please visit the{' '}
                      <a href="https://policies.google.com/privacy" link-type="url">
                        Google Privacy &amp; Terms page
                      </a>
                      .
                    </p>

                    <h4 id="sociallogin" className="pt-3">
                      6. HOW DO WE HANDLE YOUR SOCIAL LOGINS?
                    </h4>
                    <p>
                      <i>
                        <b>In Short:</b> If you choose to register or log in to our Services using a social
                        media account, we may have access to certain information about you.
                      </i>
                    </p>
                    <p>
                      Our Services offer you the ability to register and log in using your third-party social media
                      account details (like your Facebook or X logins). Where you choose to do this, we will
                      receive certain profile information about you from your social media provider. The profile
                      information we receive may vary depending on the social media provider concerned, but will
                      often include your name, email address, friends list, and profile picture, as well as other
                      information you choose to make public on such a social media platform.
                    </p>
                    <p>
                      We will use the information we receive only for the purposes that are described in this
                      Privacy Notice or that are otherwise made clear to you on the relevant Services. Please note
                      that we do not control, and are not responsible for, other uses of your personal information
                      by your third-party social media provider. We recommend that you review their privacy notice
                      to understand how they collect, use, and share your personal information, and how you can set
                      your privacy preferences on their sites and apps.
                    </p>

                    <h4 id="information" className="pt-3">
                      7. HOW LONG DO WE KEEP YOUR INFORMATION?
                    </h4>
                    <p>
                      <i>
                        <b>In Short:</b> We keep your information for as long as necessary to fulfil the
                        purposes outlined in this Privacy Notice unless otherwise required by law.
                      </i>
                    </p>
                    <p>
                      We will only keep your personal information for as long as it is necessary for the purposes
                      set out in this Privacy Notice, unless a longer retention period is required or permitted by
                      law (such as tax, accounting, or other legal requirements). No purpose in this notice will
                      require us to keep your personal information for longer than the period of time in which
                      users have an account with us.
                    </p>
                    <p>
                      When we have no ongoing legitimate business need to process your personal information, we
                      will either delete or anonymise such information, or, if this is not possible (for example,
                      because your personal information has been stored in backup archives), then we will securely
                      store your personal information and isolate it from any further processing until deletion is
                      possible.
                    </p>

                    <h4 id="informationsafe" className="pt-3">
                      8. HOW DO WE KEEP YOUR INFORMATION SAFE?
                    </h4>
                    <p>
                      <i>
                        <b>In Short:</b> We aim to protect your personal information through a system of
                        organisational and technical security measures.
                      </i>
                    </p>
                    <p>
                      We have implemented appropriate and reasonable technical and organisational security measures
                      designed to protect the security of any personal information we process. However, despite our
                      safeguards and efforts to secure your information, no electronic transmission over the
                      Internet or information storage technology can be guaranteed to be 100% secure, so we cannot
                      promise or guarantee that hackers, cybercriminals, or other unauthorised third parties will
                      not be able to defeat our security and improperly collect, access, steal, or modify your
                      information. Although we will do our best to protect your personal information, transmission
                      of personal information to and from our Services is at your own risk. You should only access
                      the Services within a secure environment.
                    </p>

                    <h4 id="informationminors" className="pt-3">
                      9. DO WE COLLECT INFORMATION FROM MINORS?
                    </h4>
                    <p>
                      <i>
                        <b>In Short:</b> We do not knowingly collect data from or market to children under 18
                        years of age or the equivalent age as specified by law in your jurisdiction.
                      </i>
                    </p>
                    <p>
                      We do not knowingly collect, solicit data from, or market to children under 18 years of age
                      or the equivalent age as specified by law in your jurisdiction, nor do we knowingly sell
                      such personal information. By using the Services, you represent that you are at least 18 or
                      the equivalent age as specified by law in your jurisdiction or that you are the parent or
                      guardian of such a minor and consent to such minor dependent&rsquo;s use of the Services. If
                      we learn that personal information from users less than 18 years of age or the equivalent age
                      as specified by law in your jurisdiction has been collected, we will deactivate the account
                      and take reasonable measures to promptly delete such data from our records. If you become
                      aware of any data we may have collected from children under age 18 or the equivalent age as
                      specified by law in your jurisdiction, please contact us at{' '}
                      <a href="mailto:reservations@bookitplease.com" link-type="email">
                        reservations@bookitplease.com
                      </a>
                      .
                    </p>

                    <h4 id="privacyrights" className="pt-3">
                      10. WHAT ARE YOUR PRIVACY RIGHTS?
                    </h4>
                    <p>
                      <i>
                        <b>In Short:</b> Depending on your state of residence in the US or in some regions,
                        such as the European Economic Area (EEA), United Kingdom (UK), Switzerland, and Canada,
                        you have rights that allow you greater access to and control over your personal
                        information. You may review, change, or terminate your account at any time, depending on
                        your country, province, or state of residence.
                      </i>
                    </p>
                    <p>
                      In some regions (like the EEA, UK, Switzerland, and Canada), you have certain rights under
                      applicable data protection laws. These may include the right (i) to request access and obtain
                      a copy of your personal information, (ii) to request rectification or erasure; (iii) to
                      restrict the processing of your personal information; (iv) if applicable, to data
                      portability; and (v) not to be subject to automated decision-making. If a decision that
                      produces legal or similarly significant effects is made solely by automated means, we will
                      inform you, explain the main factors, and offer a simple way to request human review. In
                      certain circumstances, you may also have the right to object to the processing of your
                      personal information. You can make such a request by contacting us by using the contact
                      details provided in the section <a href="#updatenotice">&apos;HOW CAN YOU CONTACT US ABOUT THIS NOTICE?&apos;</a>{' '}
                      below.
                    </p>
                    <p>We will consider and act upon any request in accordance with applicable data protection laws.</p>
                    <p>
                      If you are located in the EEA or UK and you believe we are unlawfully processing your personal
                      information, you also have the right to complain to your{' '}
                      <a href="https://ec.europa.eu/newsroom/article29/items/612080" link-type="url">
                        Member State data protection authority
                      </a>{' '}
                      or{' '}
                      <a href="https://ico.org.uk/make-a-complaint/data-protection-complaints/" link-type="url">
                        UK data protection authority
                      </a>
                      .
                    </p>
                    <p>
                      If you are located in Switzerland, you may contact the{' '}
                      <a href="https://www.edoeb.admin.ch/en" link-type="url">
                        Federal Data Protection and Information Commissioner
                      </a>
                      .
                    </p>
                    <p>
                      <a href="#informationminors">Withdrawing your consent:</a> If we are relying on your consent
                      to process your personal information, which may be express and/or implied consent depending
                      on the applicable law, you have the right to withdraw your consent at any time. You can
                      withdraw your consent at any time by contacting us by using the contact details provided in
                      the section <a href="#updatenotice">&apos;HOW CAN YOU CONTACT US ABOUT THIS NOTICE?&apos;</a>{' '}
                      below or updating your preferences.
                    </p>
                    <p>
                      However, please note that this will not affect the lawfulness of the processing before its
                      withdrawal nor, when applicable law allows, will it affect the processing of your personal
                      information conducted in reliance on lawful processing grounds other than consent.
                    </p>
                    <p>
                      Opting out of marketing and promotional communications: You can unsubscribe from our
                      marketing and promotional communications at any time by clicking on the unsubscribe link in
                      the emails that we send, or by contacting us using the details provided in the section &apos;HOW
                      CAN YOU CONTACT US ABOUT THIS NOTICE?&apos; below. You will then be removed from the marketing
                      lists. However, we may still communicate with you — for example, to send you service-related
                      messages that are necessary for the administration and use of your account, to respond to
                      service requests, or for other non-marketing purposes.
                    </p>
                    <h6>Account Information</h6>
                    <p>
                      If you would at any time like to review or change the information in your account or
                      terminate your account, you can:
                    </p>
                    <ul>
                      <li>
                        Email us on{' '}
                        <a href="mailto:reservations@bookitplease.com" link-type="email">
                          reservations@bookitplease.com
                        </a>
                      </li>
                    </ul>
                    <p>
                      Upon your request to terminate your account, we will deactivate or delete your account and
                      information from our active databases. However, we may retain some information in our files
                      to prevent fraud, troubleshoot problems, assist with any investigations, enforce our legal
                      terms and/or comply with applicable legal requirements.
                    </p>
                    <p>
                      <b>Cookies and similar technologies:</b> Most Web browsers are set to accept cookies by
                      default. If you prefer, you can usually choose to set your browser to remove cookies and to
                      reject cookies. If you choose to remove cookies or reject cookies, this could affect certain
                      features or services of our Services.
                    </p>
                    <p>
                      If you have questions or comments about your privacy rights, you may email us on{' '}
                      <a href="mailto:reservations@bookitplease.com" link-type="email">
                        reservations@bookitplease.com
                      </a>
                    </p>

                    <h4 id="controls" className="pt-3">
                      11. CONTROLS FOR DO-NOT-TRACK FEATURES
                    </h4>
                    <p>
                      Most web browsers and some mobile operating systems and mobile applications include a
                      Do-Not-Track (&apos;DNT&apos;) feature or setting you can activate to signal your privacy
                      preference not to have data about your online browsing activities monitored and collected. At
                      this stage, no uniform technology standard for recognising and implementing DNT signals has
                      been finalised. As such, we do not currently respond to DNT browser signals or any other
                      mechanism that automatically communicates your choice not to be tracked online. If a standard
                      for online tracking is adopted that we must follow in the future, we will inform you about
                      that practice in a revised version of this Privacy Notice.
                    </p>
                    <p>
                      California law requires us to let you know how we respond to web browser DNT signals. Because
                      there currently is not an industry or legal standard for recognising or honouring DNT signals,
                      we do not respond to them at this time.
                    </p>

                    <h4 id="usresidents" className="pt-3">
                      12. DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
                    </h4>
                    <p>
                      <i>
                        <b>In Short:</b> If you are a resident of California, Colorado, Connecticut, Delaware,
                        Florida, Indiana, Iowa, Kentucky, Maryland, Minnesota, Montana, Nebraska, New Hampshire,
                        New Jersey, Oregon, Rhode Island, Tennessee, Texas, Utah, or Virginia, you may have the
                        right to request access to and receive details about the personal information we maintain
                        about you and how we have processed it, correct inaccuracies, get a copy of, or delete your
                        personal information. You may also have the right to withdraw your consent to our processing
                        of your personal information. These rights may be limited in some circumstances by
                        applicable law. More information is provided below.
                      </i>
                    </p>
                    <h6>Categories of Personal Information We Collect</h6>
                    <p>
                      The table below shows the categories of personal information we have collected in the past
                      twelve (12) months. The table includes illustrative examples of each category and does not
                      reflect the personal information we collect from you.
                    </p>
                    <table className="table table-bordered">
                      <thead className="thead-dark">
                        <tr>
                          <th scope="col">Category</th>
                          <th scope="col">Examples</th>
                          <th scope="col">Collected</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td scope="row">A. Identifiers</td>
                          <td>
                            Contact details, such as real name, alias, postal address, telephone or mobile contact
                            number, unique personal identifier, online identifier, Internet Protocol address, email
                            address, and account name
                          </td>
                          <td>YES</td>
                        </tr>
                        <tr>
                          <td scope="row">B. Personal information as defined in the California Customer Records statute</td>
                          <td>Name, contact information, education, employment, employment history, and financial information</td>
                          <td>NO</td>
                        </tr>
                        <tr>
                          <td scope="row">C. Protected classification characteristics under state or federal law</td>
                          <td>Gender, age, date of birth, race and ethnicity, national origin, marital status, and other demographic data</td>
                          <td>NO</td>
                        </tr>
                        <tr>
                          <td scope="row">D. Commercial information</td>
                          <td>Transaction information, purchase history, financial details, and payment information</td>
                          <td>NO</td>
                        </tr>
                        <tr>
                          <td scope="row">E. Biometric information</td>
                          <td>Fingerprints and voiceprints</td>
                          <td>NO</td>
                        </tr>
                        <tr>
                          <td scope="row">F. Internet or other similar network activity</td>
                          <td>Browsing history, search history, online behaviour, interest data, and interactions with our and other websites, applications, systems, and advertisements</td>
                          <td>NO</td>
                        </tr>
                        <tr>
                          <td scope="row">G. Geolocation data</td>
                          <td>Device location</td>
                          <td>NO</td>
                        </tr>
                        <tr>
                          <td scope="row">H. Audio, electronic, sensory, or similar information</td>
                          <td>Images and audio, video or call recordings created in connection with our business activities</td>
                          <td>NO</td>
                        </tr>
                        <tr>
                          <td scope="row">I. Professional or employment-related information</td>
                          <td>Business contact details in order to provide you our Services at a business level or job title, work history, and professional qualifications if you apply for a job with us</td>
                          <td>NO</td>
                        </tr>
                        <tr>
                          <td scope="row">J. Education Information</td>
                          <td>Student records and directory information</td>
                          <td>NO</td>
                        </tr>
                        <tr>
                          <td scope="row">K. Inferences drawn from collected personal information</td>
                          <td>Inferences drawn from any of the collected personal information listed above to create a profile or summary about, for example, an individual’s preferences and characteristics</td>
                          <td>NO</td>
                        </tr>
                        <tr>
                          <td scope="row">L. Sensitive personal Information</td>
                          <td></td>
                          <td>NO</td>
                        </tr>
                      </tbody>
                    </table>
                    <p>
                      We may also collect other personal information outside of these categories through instances
                      where you interact with us in person, online, or by phone or mail in the context of:
                    </p>
                    <ul>
                      <li>Receiving help through our customer support channels;</li>
                      <li>Participation in customer surveys or contests; and</li>
                      <li>Facilitation in the delivery of our Services and to respond to your inquiries.</li>
                    </ul>
                    <p>We will use and retain the collected personal information as needed to provide the Services or for:</p>
                    <ul>
                      <li>Category H - As long as the user has an account with us</li>
                    </ul>
                    <h6>How We Use and Share Personal Information</h6>
                    <p>
                      Learn more about how we use your personal information in the section,{' '}
                      <a href="#informationprocess">&apos;HOW DO WE PROCESS YOUR INFORMATION?&apos;</a>
                    </p>
                    <h6>Will your information be shared with anyone else?</h6>
                    <p>
                      We may disclose your personal information with our service providers pursuant to a written
                      contract between us and each service provider. Learn more about how we disclose personal
                      information to in the section,{' '}
                      <a href="#personalinformation">
                        &apos;WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?&apos;
                      </a>
                    </p>
                    <p>
                      We may use your personal information for our own business purposes, such as for undertaking
                      internal research for technological development and demonstration. This is not considered to
                      be &apos;selling&apos; your personal information.
                    </p>
                    <p>
                      We have not disclosed, sold, or shared any personal information to third parties for a business
                      or commercial purpose in the preceding twelve (12) months. We will not sell or share personal
                      information in the future belonging to website visitors, users, and other consumers.
                    </p>
                    <h6>Your Rights</h6>
                    <p>
                      You have rights under certain US state data protection laws. However, these rights are not
                      absolute, and in certain cases, we may decline your request as permitted by law. These rights
                      include:
                    </p>
                    <ul>
                      <li>Right to know whether or not we are processing your personal data</li>
                      <li>Right to access your personal data</li>
                      <li>Right to correct inaccuracies in your personal data</li>
                      <li>Right to request the deletion of your personal data</li>
                      <li>Right to obtain a copy of the personal data you previously shared with us</li>
                      <li>Right to non-discrimination for exercising your rights</li>
                      <li>
                        Right to opt out of the processing of your personal data if it is used for targeted
                        advertising (or sharing as defined under California’s privacy law), the sale of personal
                        data, or profiling in furtherance of decisions that produce legal or similarly significant
                        effects (&apos;profiling&apos;)
                      </li>
                    </ul>
                    <p>Depending upon the state where you live, you may also have the following rights:</p>
                    <ul>
                      <li>Right to access the categories of personal data being processed (as permitted by applicable law, including the privacy law in Minnesota)</li>
                      <li>Right to obtain a list of the categories of third parties to which we have disclosed personal data (as permitted by applicable law, including the privacy law in California, Delaware, and Maryland)</li>
                      <li>Right to obtain a list of specific third parties to which we have disclosed personal data (as permitted by applicable law, including the privacy law in Minnesota and Oregon)</li>
                      <li>Right to review, understand, question, and correct how personal data has been profiled (as permitted by applicable law, including the privacy law in Minnesota)</li>
                      <li>Right to limit use and disclosure of sensitive personal data (as permitted by applicable law, including the privacy law in California)</li>
                      <li>Right to opt out of the collection of sensitive data and personal data collected through the operation of a voice or facial recognition feature (as permitted by applicable law, including the privacy law in Florida)</li>
                    </ul>
                    <h6>How to Exercise Your Rights</h6>
                    <p>
                      To exercise these rights, you can contact us by submitting a{' '}
                      <a href="https://docs.google.com/document/d/1hQkBpjoiIjMwewNx6NVtFI2Xl2X3NzcDmXZXaz_UV04/edit?usp=sharing" link-type="url">
                        data subject access request
                      </a>
                      , by emailing us at{' '}
                      <a href="mailto:reservations@bookitplease.com" link-type="email">
                        reservations@bookitplease.com
                      </a>
                      , or by referring to the contact details at the bottom of this document.
                    </p>
                    <p>
                      Under certain US state data protection laws, you can designate an authorised agent to make a
                      request on your behalf. We may deny a request from an authorised agent that does not submit
                      proof that they have been validly authorised to act on your behalf in accordance with
                      applicable laws.
                    </p>
                    <h6>Request Verification</h6>
                    <p>
                      Upon receiving your request, we will need to verify your identity to determine you are the
                      same person about whom we have the information in our system. We will only use personal
                      information provided in your request to verify your identity or authority to make the request.
                      However, if we cannot verify your identity from the information already maintained by us, we may
                      request that you provide additional information for the purposes of verifying your identity and
                      for security or fraud-prevention purposes.
                    </p>
                    <p>
                      If you submit the request through an authorised agent, we may need to collect additional
                      information to verify your identity before processing your request and the agent will need to
                      provide a written and signed permission from you to submit such request on your behalf.
                    </p>
                    <h6>Appeals</h6>
                    <p>
                      Under certain US state data protection laws, if we decline to take action regarding your
                      request, you may appeal our decision by emailing us at{' '}
                      <a href="mailto:reservations@bookitplease.com" link-type="email">
                        reservations@bookitplease.com
                      </a>
                      . We will inform you in writing of any action taken or not taken in response to the appeal,
                      including a written explanation of the reasons for the decisions. If your appeal is denied,
                      you may submit a complaint to your state attorney general.
                    </p>
                    <h6>California &apos;Shine The Light&apos; Law</h6>
                    <p>
                      California Civil Code Section 1798.83, also known as the &apos;Shine The Light&apos; law,
                      permits our users who are California residents to request and obtain from us, once a year and
                      free of charge, information about categories of personal information (if any) we disclosed to
                      third parties for direct marketing purposes and the names and addresses of all third parties
                      with which we shared personal information in the immediately preceding calendar year. If you
                      are a California resident and would like to make such a request, please submit your request in
                      writing to us by using the contact details provided in the section{' '}
                      <a href="#data">&apos;HOW CAN YOU CONTACT US ABOUT THIS NOTICE?&apos;</a>
                    </p>

                    <h4 id="specificprivacy" className="pt-3">
                      13. DO OTHER REGIONS HAVE SPECIFIC PRIVACY RIGHTS?
                    </h4>
                    <p>
                      <i>
                        <b>In Short:</b> You may have additional rights based on the country you reside in.
                      </i>
                    </p>
                    <h6>Australia and New Zealand</h6>
                    <p>
                      We collect and process your personal information under the obligations and conditions set by
                      Australia&apos;s Privacy Act 1988 and New Zealand&apos;s Privacy Act 2020 (Privacy Act).
                    </p>
                    <p>
                      This Privacy Notice satisfies the notice requirements defined in both Privacy Acts, in
                      particular: what personal information we collect from you, from which sources, for which
                      purposes, and other recipients of your personal information.
                    </p>
                    <p>If you do not wish to provide the personal information necessary to fulfil their applicable purpose, it may affect our ability to provide our services, in particular:</p>
                    <ul>
                      <li>offer you the products or services that you want</li>
                      <li>respond to or help with your requests</li>
                      <li>manage your account with us</li>
                      <li>confirm your identity and protect your account</li>
                    </ul>
                    <p>At any time, you have the right to request access to or correction of your personal information. You can make such a request by contacting us by using the contact details provided in the section &apos;HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?&apos;</p>
                    <p>
                      If you believe we are unlawfully processing your personal information, you have the right to
                      submit a complaint about a breach of the Australian Privacy Principles to the{' '}
                      <a href="https://www.oaic.gov.au/privacy/privacy-complaints/lodge-a-privacy-complaint-with-us" link-type="url">
                        Office of the Australian Information Commissioner
                      </a>{' '}
                      and a breach of New Zealand&apos;s Privacy Principles to the{' '}
                      <a href="https://www.privacy.org.nz/your-rights/making-a-complaint-to-the-privacy-commissioner/" link-type="url">
                        Office of New Zealand Privacy Commissioner
                      </a>
                      .
                    </p>
                    <h6>Republic of South Africa</h6>
                    <p>
                      At any time, you have the right to request access to or correction of your personal
                      information. You can make such a request by contacting us by using the contact details
                      provided in the section <a href="#updatenotice">&apos;HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?&apos;</a>
                    </p>
                    <p>
                      If you are unsatisfied with the manner in which we address any complaint with regard to our
                      processing of personal information, you can contact the office of the regulator, the details
                      of which are:{' '}
                      <a href="https://inforegulator.org.za/" link-type="url">
                        The Information Regulator (South Africa)
                      </a>{' '}
                      General enquiries: <a href="mailto:enquiries@inforegulator.org.za" link-type="email">enquiries@inforegulator.org.za</a>{' '}
                      Complaints (complete POPIA/PAIA form 5):{' '}
                      <a href="mailto:PAIAComplaints@inforegulator.org.za" link-type="email">PAIAComplaints@inforegulator.org.za</a> &amp;{' '}
                      <a href="mailto:POPIAComplaints@inforegulator.org.za" link-type="email">POPIAComplaints@inforegulator.org.za</a>
                    </p>

                    <h4 id="updatenotice" className="pt-3">
                      14. DO WE MAKE UPDATES TO THIS NOTICE?
                    </h4>
                    <p>
                      <i>
                        <b>In Short:</b> Yes, we will update this notice as necessary to stay compliant with
                        relevant laws.
                      </i>
                    </p>
                    <p>
                      We may update this Privacy Notice from time to time. The updated version will be indicated by
                      an updated &apos;Revised&apos; date at the top of this Privacy Notice. If we make material
                      changes to this Privacy Notice, we may notify you either by prominently posting a notice of such
                      changes or by directly sending you a notification. We encourage you to review this Privacy
                      Notice frequently to be informed of how we are protecting your information.
                    </p>

                    <h4 id="contactnotice" className="pt-3">
                      15. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
                    </h4>
                    <p>
                      If you have questions or comments about this notice, you may contact our Data Protection Officer
                      (DPO) by email at <a href="mailto:reservations@bookitplease.com" link-type="email">reservations@bookitplease.com</a> or by phone at
                      00442039721545.
                    </p>

                    <h4 id="data" className="pt-3">
                      16. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
                    </h4>
                    <p>
                      Based on the applicable laws of your country or state of residence in the US, you may have the
                      right to request access to the personal information we collect from you, details about how we have
                      processed it, correct inaccuracies, or delete your personal information. You may also have the
                      right to withdraw your consent to our processing of your personal information. These rights may be
                      limited in some circumstances by applicable law. To request to review, update, or delete your
                      personal information, please fill out and submit a{' '}
                      <a href="https://docs.google.com/document/d/1hQkBpjoiIjMwewNx6NVtFI2Xl2X3NzcDmXZXaz_UV04/edit?usp=sharing&amp;pli=1&amp;authuser=0" link-type="url">
                        data subject access request
                      </a>
                      .
                    </p>
                  </div>
                </div>

                <div role="tabpanel" id="theatre" className={`tab-content ${activeTab === 'theatre' ? 'active' : ''}`}>
                  <h2>Theatre Privacy Policy</h2>
                  <p>Last Updated: 7 April 2026</p>

                  <h4 className="pt-3">1. Who we are</h4>
                  <p>
                    You should know who decides how and why your data is processed (the data controller) and how
                    to contact them. GHS Global Hospitality Ltd (Book It Please) is the data controller. We
                    are the people responsible for protecting your data.
                  </p>
                  <ul>
                    <li>Entity: GHS Global Hospitality Ltd (Book It Please)</li>
                    <li>Registered in England &amp; Wales: No. 11055738</li>
                    <li>Registered Office: 10 Norwich Street, London, EC4A 1BD</li>
                    <li>Data Controller: GHS Global Hospitality Ltd</li>
                    <li>
                      Contact:{' '}
                      <a href="mailto:it@bookitplease.com" link-type="email">
                        it@bookitplease.com
                      </a>
                    </li>
                  </ul>

                  <hr />

                  <h4 className="pt-3">2. When this policy applies</h4>
                  <p>
                    This policy applies whenever you interact with Book It Please (website, account creation,
                    bookings, support, or any use of our platform).
                  </p>
                  <ul>
                    <li>
                      Scope: All Personal Data processed in the context of website use, account creation,
                      bookings (hotels, theatre tickets, or other services), customer support, and use of any
                      part of the Book It Please platform.
                    </li>
                  </ul>

                  <hr />

                  <h4 className="pt-3">3. What information we collect</h4>
                  <p>
                    We collect data to provide services, verify identity, process bookings, and communicate
                    with you. We collect information you give us, data we collect automatically, and social login
                    data.
                  </p>
                  <ul>
                    <li>
                      <strong>3.1 Information You Provide:</strong> name, email address, telephone number,
                      address and demographic data, payment info, account credentials, and communications.
                    </li>
                    <li>
                      <strong>3.2 Information Collected Automatically:</strong> IP address, device and
                      browser info, site usage data.
                    </li>
                    <li>
                      <strong>3.3 Social Login Data:</strong> limited profile information when using social login.
                    </li>
                    <li>
                      <strong>3.4 Sensitive Personal Data:</strong> We do not intentionally collect sensitive
                      personal data.
                    </li>
                  </ul>

                  <hr />

                  <h4 className="pt-3">4. How we use your information</h4>
                  <p>
                    We process your data to manage accounts, process bookings, send confirmations, provide
                    support, improve our services, and meet legal requirements.
                  </p>
                  <p>
                    <strong>AI-supported services (with safeguards):</strong> We may use automated tools to
                    respond to queries and analyze performance. These tools use existing data, are monitored by
                    humans, and do not train external AI models on your data.
                  </p>

                  <hr />

                  <h4 className="pt-3">5. Why are we allowed to use your data?</h4>
                  <p>
                    We rely on lawful bases to process data: Contract performance (fulfil bookings), Consent
                    (for marketing), Legitimate interests (improve services, security), and Legal obligations.
                  </p>

                  <hr />

                  <h4 className="pt-3">6. Who do we share your data with?</h4>
                  <p>
                    We share data only with necessary partners to deliver bookings (e.g. hotel or theatre
                    providers) and technological providers that host our platform. We do not sell your personal data.
                  </p>

                  <hr />

                  <h4 className="pt-3">7. Cookies</h4>
                  <p>
                    We use cookies to keep the site working, understand how people use it, and improve your
                    experience. See our Cookie Policy for details.
                  </p>

                  <hr />

                  <h4 className="pt-3">8. How long we keep your data</h4>
                  <p>
                    We keep data as long as needed: while your account is active, for bookings and legal
                    purposes, and for marketing until you opt out.
                  </p>

                  <hr />

                  <h4 className="pt-3">9. How we keep your data safe</h4>
                  <p>
                    We implement technical and organizational measures (encryption, secure servers) to protect
                    data. We acknowledge that no system is 100% secure.
                  </p>

                  <hr />

                  <h4 className="pt-3">10. International transfers</h4>
                  <p>
                    Your data may be processed outside the UK/EEA. When this occurs, appropriate safeguards
                    and transfer mechanisms are enforced.
                  </p>

                  <hr />

                  <h4 className="pt-3">11. Your rights</h4>
                  <p>
                    You can access, correct, delete, restrict, or object to processing. To exercise these
                    rights, contact us at{' '}
                    <a href="mailto:it@bookitplease.com" link-type="email">
                      it@bookitplease.com
                    </a>
                    .
                  </p>

                  <hr />

                  <h4 className="pt-3">12. Marketing communications</h4>
                  <p>
                    If you opt in, we may send you offers and promotions. You can unsubscribe anytime using the
                    link in our emails.
                  </p>

                  <hr />

                  <h4 className="pt-3">13. Children</h4>
                  <p>We do not knowingly collect data from users under 18.</p>

                  <hr />

                  <h4 className="pt-3">14. Third-party links</h4>
                  <p>
                    When you navigate to external sites via our platform, their third-party privacy policies apply.
                  </p>

                  <hr />

                  <h4 className="pt-3">15. Terms &amp; Conditions</h4>
                  <p>This Privacy Policy works alongside our Terms &amp; Conditions.</p>

                  <hr />

                  <h4 className="pt-3">16. Policy updates</h4>
                  <p>We may update this policy from time to time. The updated version will be posted on our site.</p>

                  <hr />

                  <h4 className="pt-3">17. Contact</h4>
                  <p>
                    Reach us with data protection questions at{' '}
                    <a href="mailto:it@bookitplease.com" link-type="email">
                      it@bookitplease.com
                    </a>{' '}
                    or +44 203 972 1545.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
